import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Eye,
  FileCheck,
  FileText,
  FolderOpen,
  Trash2,
  User,
  Loader2,
} from "lucide-react";
import { differenceInDays, format } from "date-fns";
import {
  getAllContracts,
  deleteContract,
} from "../../../api/crm/contracts/info";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";
import Pagination from "../../../components/common/Pagination";
import ContractFilter from "./ContractFilter";
import ContractCreateModal from "./ContractCreateModal";
import ContractPreviewModal from "./ContractPreviewModal";
import ContractMiniDashboard from "./ContractMiniDashboard";

const statusClassMap = {
  "Đang thực hiện": "bg-blue-50 text-blue-700 border-blue-100",
  "Hoàn thành": "bg-emerald-50 text-emerald-700 border-emerald-100",
  "Chậm tiến độ": "bg-red-50 text-red-700 border-red-100",
  "Sắp hết hạn": "bg-amber-50 text-amber-700 border-amber-100",
};

const isExpiringSoon = (contract) => {
  const expiryDate = contract.end_date ? new Date(contract.end_date) : null;
  if (!expiryDate) return false;
  const daysToExpiry = differenceInDays(expiryDate, new Date());
  return daysToExpiry >= 0 && daysToExpiry <= 30;
};

const ContractListPage = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState({
    keyword: "",
    status: "",
    branch_id: "",
    customer_id: "",
    warning: null,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const canUpdate = hasPermission("crm.contracts", "U");
  const canDelete = hasPermission("crm.contracts", "D");
  const canViewDocuments = hasPermission("crm.documents", "R");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await getAllContracts({ limit: 500 });
      setContracts(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      console.error("Lỗi khi lấy danh sách hợp đồng:", err);
      showErrorToast("Không thể tải danh sách hợp đồng");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((c) => {
      const keyword = filters.keyword?.toLowerCase() || "";
      const searchStr =
        `${c.contract_code || ""} ${c.title || ""} ${c.customer_name || ""}`.toLowerCase();

      if (keyword && !searchStr.includes(keyword)) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.branch_id && c.branch_id !== filters.branch_id) return false;
      if (filters.customer_id && c.customer_id !== filters.customer_id) return false;

      if (filters.warning === "expiring") return isExpiringSoon(c);
      if (filters.warning === "unfinished") return c.status !== "Hoàn thành";
      if (filters.warning === "with_documents") return (c.document_count || 0) > 0;

      return true;
    });
  }, [contracts, filters]);

  const dashboardStats = useMemo(() => {
    return {
      total: contracts.length,
      unfinished: contracts.filter((c) => c.status !== "Hoàn thành").length,
      expiring: contracts.filter(isExpiringSoon).length,
      with_documents: contracts.filter((c) => (c.document_count || 0) > 0).length,
    };
  }, [contracts]);

  const handleDashboardCardClick = (key) => {
    setFilters((prev) => {
      if (key === "total") {
        return { ...prev, warning: null };
      }
      // Toggle: click lại cùng thẻ → bỏ lọc
      if (prev.warning === key) {
        return { ...prev, warning: null };
      }
      return { ...prev, warning: key };
    });
  };

  const paginatedContracts = filteredContracts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleEdit = (id) => {
    setEditId(id);
    setShowCreateModal(true);
  };

  const handlePreview = (contract) => {
    setSelectedContract(contract);
    setShowPreviewModal(true);
  };

  const handleDelete = async (id, contractName) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.contract,
      name: contractName,
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteContract(id);
      showSuccessToast("Đã xoá hợp đồng");
      fetchContracts();
    } catch (err) {
      console.error(err);
      showErrorToast("Lỗi khi xoá hợp đồng");
    }
  };

  const openCreateModal = () => {
    setEditId(null);
    setShowCreateModal(true);
  };

  const goToDocuments = (contractId) => {
    if (!canViewDocuments) return;
    const path = contractId
      ? `/crm/documents?contract_id=${contractId}`
      : "/crm/documents";
    navigate(path);
  };

  const renderActions = (contract) => (
    <div className="flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => handlePreview(contract)}
        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
        title="Xem trước"
      >
        <Eye size={17} />
      </button>
      {canUpdate && (
        <button
          type="button"
          onClick={() => handleEdit(contract.id)}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          title="Chỉnh sửa"
        >
          <FileText size={17} />
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={() =>
            handleDelete(contract.id, contract.title || contract.contract_code)
          }
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          title="Xóa"
        >
          <Trash2 size={17} />
        </button>
      )}
    </div>
  );

  const renderContractCard = (contract) => {
    const daysRemaining = contract.end_date
      ? differenceInDays(new Date(contract.end_date), new Date())
      : null;
    const isExpiring =
      daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 30;
    const docCount = contract.document_count || 0;

    return (
      <div
        key={contract.id}
        className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase text-red-700">
              {contract.contract_code || "N/A"}
            </span>
            <h3 className="mt-2 text-sm font-bold text-gray-900">{contract.title || "Không có tiêu đề"}</h3>
            <p className="mt-1 text-xs text-gray-500">{contract.customer_name}</p>
          </div>
          <span
            className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${
              statusClassMap[contract.status] || "bg-gray-50 text-gray-600 border-gray-100"
            }`}
          >
            {contract.status || "—"}
          </span>
        </div>

        <div className="mt-3 space-y-1 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Building2 size={12} className="text-red-500" />
            {contract.branch_name || "Chưa có chi nhánh"}
          </div>
          <div>
            {contract.start_date ? format(new Date(contract.start_date), "dd/MM/yyyy") : "--"}
            {" → "}
            <span className={isExpiring ? "font-bold text-red-600" : ""}>
              {contract.end_date ? format(new Date(contract.end_date), "dd/MM/yyyy") : "--"}
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-gray-50 pt-3">
          {canViewDocuments ? (
            <button
              type="button"
              onClick={() => goToDocuments(contract.id)}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-700"
            >
              <FolderOpen size={12} />
              {docCount} hồ sơ
            </button>
          ) : (
            <span className="text-[10px] text-gray-400">{docCount} hồ sơ</span>
          )}
          {renderActions(contract)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="contracts-header">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <FileCheck className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Quản lý <span className="text-red-700">hợp đồng</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Soạn thảo, theo dõi và quản lý vòng đời hợp đồng
              </p>
            </div>
          </div>
        </div>
      </div>

      <ContractMiniDashboard
        stats={dashboardStats}
        loading={loading}
        activeKey={filters.warning}
        onCardClick={handleDashboardCardClick}
      />

      <ContractFilter
        filters={filters}
        setFilters={setFilters}
        onRefresh={fetchContracts}
        onNewClick={openCreateModal}
      />

      {isMobile ? (
        <div className="space-y-3">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-9 w-9 animate-spin text-red-600" />
              <p className="text-sm font-semibold text-gray-500">Đang tải hợp đồng...</p>
            </div>
          ) : paginatedContracts.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-white py-14">
              <FileText className="h-10 w-10 text-gray-300" />
              <p className="text-sm font-semibold text-gray-500">Không tìm thấy hợp đồng phù hợp</p>
            </div>
          ) : (
            paginatedContracts.map((contract) => renderContractCard(contract))
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-left">
                  <th className="p-4 text-xs font-bold uppercase tracking-wider text-gray-400">Mã hợp đồng</th>
                  <th className="p-4 min-w-[240px] text-xs font-bold uppercase tracking-wider text-gray-400">Tiêu đề</th>
                  <th className="p-4 min-w-[200px] text-xs font-bold uppercase tracking-wider text-gray-400">Khách hàng</th>
                  <th className="p-4 min-w-[180px] text-xs font-bold uppercase tracking-wider text-gray-400">Thời gian</th>
                  <th className="p-4 min-w-[120px] text-xs font-bold uppercase tracking-wider text-gray-400">Trạng thái</th>
                  <th className="p-4 min-w-[100px] text-xs font-bold uppercase tracking-wider text-gray-400">Hồ sơ</th>
                  <th className="p-4 w-[140px] text-center text-xs font-bold uppercase tracking-wider text-gray-400">Thao tác</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="p-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-9 w-9 animate-spin text-red-600" />
                          <p className="text-sm font-semibold text-gray-500">Đang tải dữ liệu hợp đồng...</p>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedContracts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-14 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <FileText className="h-10 w-10 text-gray-300" />
                          <p className="text-sm font-semibold text-gray-500">Không tìm thấy hợp đồng phù hợp</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedContracts.map((contract) => {
                      const daysRemaining = contract.end_date
                        ? differenceInDays(new Date(contract.end_date), new Date())
                        : null;
                      const isExpiring =
                        daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 30;
                      const docCount = contract.document_count || 0;

                      return (
                        <tr
                          key={contract.id}
                          className="group transition-colors hover:bg-red-50/30"
                        >
                          <td className="p-4 align-top">
                            <div className="inline-flex rounded-full bg-red-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-red-700">
                              {contract.contract_code || "N/A"}
                            </div>
                          </td>

                          <td className="p-4 align-top">
                            <div className="space-y-2">
                              <div className="font-bold text-gray-900 group-hover:text-red-600">
                                {contract.title || "Không có tiêu đề"}
                              </div>
                              <div className="flex items-center gap-2 text-[11px] font-medium text-gray-500">
                                <Building2 size={13} className="text-red-500" />
                                <span>{contract.branch_name || "Chưa có chi nhánh"}</span>
                              </div>
                            </div>
                          </td>

                          <td className="p-4 align-top">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white bg-slate-100 text-slate-600">
                                <User size={15} />
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-800">
                                  {contract.customer_name || "Chưa chọn khách hàng"}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="p-4 align-top">
                            <div className="space-y-2">
                              <div className="text-sm font-semibold text-gray-700">
                                {contract.start_date
                                  ? format(new Date(contract.start_date), "dd/MM/yyyy")
                                  : "--"}
                                <span className="mx-2 text-gray-300">→</span>
                                <span className={isExpiring ? "text-red-600" : "text-gray-900"}>
                                  {contract.end_date
                                    ? format(new Date(contract.end_date), "dd/MM/yyyy")
                                    : "--"}
                                </span>
                              </div>
                              {isExpiring && (
                                <span className="inline-flex rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-red-600">
                                  {daysRemaining === 0
                                    ? "Hết hạn hôm nay"
                                    : `Còn ${daysRemaining} ngày`}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="p-4 align-top">
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                statusClassMap[contract.status] ||
                                "bg-gray-50 text-gray-600 border-gray-100"
                              }`}
                            >
                              {contract.status || "Chưa cập nhật"}
                            </span>
                          </td>

                          <td className="p-4 align-top">
                            {canViewDocuments ? (
                              <button
                                type="button"
                                onClick={() => goToDocuments(contract.id)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 transition hover:bg-emerald-100"
                              >
                                <FolderOpen size={12} />
                                {docCount}
                              </button>
                            ) : (
                              <span className="text-sm font-semibold text-gray-500">{docCount}</span>
                            )}
                          </td>

                          <td className="p-4 align-top">{renderActions(contract)}</td>
                        </tr>
                      );
                    })
                  )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredContracts.length > 0 ? (
        <Pagination
          totalRecords={filteredContracts.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      ) : null}

      <ContractCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        editId={editId}
        onRefresh={fetchContracts}
      />

      <ContractPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        contract={selectedContract}
      />
    </div>
  );
};

export default ContractListPage;
